import { createContext, useBreakpointValue } from '@hover/blueprint';
import type { motion } from '@hover/blueprint/foundation';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useMemoContextValue } from 'src/shared/hooks';

import { isFullscreenImageRoute } from '../../helpers';
import { useRoutes } from '../../hooks';

type Spring = typeof motion.framer.firm;

export interface CustomizeLayoutContext {
  /**
   * Whether to run animations when the component is initially mounted
   */
  initial: boolean;
  /**
   * Whether to enable layout animations to account for vertical mobile layout
   */
  layout: boolean;
  isExitingToFullscreen: boolean;
  isLayoutAnimationComplete: boolean;
  onLayoutAnimationComplete: () => void;
  transitions: {
    layout: Spring;
    image: Spring;
    drawer: Spring;
  };
}

const [Provider, useContext] = createContext<CustomizeLayoutContext>({
  name: 'CustomizeLayoutContext',
});

export interface CustomizeLayoutProviderProps
  extends PropsWithChildren<{}>,
    Pick<
      CustomizeLayoutContext,
      'isLayoutAnimationComplete' | 'onLayoutAnimationComplete' | 'transitions'
    > {}

export const useCustomizeLayout = () => useContext();

export const CustomizeLayoutProvider = ({
  children,
  isLayoutAnimationComplete,
  onLayoutAnimationComplete,
  transitions,
}: CustomizeLayoutProviderProps) => {
  const history = useHistory();

  const { isFirstRoute, wasFullscreenImageRoute, wasCustomizeRoute } =
    useRoutes();

  const [isExitingToFullscreen, setIsExitingToFullscreen] = useState(false);

  const initial =
    !isFirstRoute && !wasFullscreenImageRoute && !wasCustomizeRoute;

  const layout = useBreakpointValue(
    {
      base: true,
      tablet: false,
    },
    { fallback: 'tablet' },
  );

  useEffect(
    () =>
      history.listen(({ pathname }) => {
        if (isFullscreenImageRoute(pathname)) setIsExitingToFullscreen(true);
      }),
    [history],
  );

  return (
    <Provider
      value={useMemoContextValue({
        initial,
        isExitingToFullscreen,
        isLayoutAnimationComplete,
        layout,
        onLayoutAnimationComplete,
        transitions,
      })}
    >
      {children}
    </Provider>
  );
};
